import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Plus, 
    Archive,
    ArrowDownCircle,
    ArrowUpCircle,
    ArrowLeftCircle,
    Package,
    FileText
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEDIDAS = ['12cm', '15cm', '18cm'];

const initialEntradaForm = {
    proveedor: '',
    fecha: new Date().toISOString().split('T')[0],
    medida: '',
    cantidad: 0,
    lote: ''
};

const initialSalidaForm = {
    fecha: new Date().toISOString().split('T')[0],
    maquilador: '',
    corte_id: ''
};

export const Cierres = () => {
    const [registros, setRegistros] = useState([]);
    const [inventario, setInventario] = useState({});
    const [cortes, setCortes] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entradaModalOpen, setEntradaModalOpen] = useState(false);
    const [salidaModalOpen, setSalidaModalOpen] = useState(false);
    const [entradaForm, setEntradaForm] = useState(initialEntradaForm);
    const [salidaForm, setSalidaForm] = useState(initialSalidaForm);
    const [selectedCorteInfo, setSelectedCorteInfo] = useState(null);
    const [tallaMedidas, setTallaMedidas] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [cierresRes, invRes, cortesRes, provRes] = await Promise.all([
                axios.get(`${API_URL}/api/cierres`),
                axios.get(`${API_URL}/api/cierres/inventario`),
                axios.get(`${API_URL}/api/cortes`),
                axios.get(`${API_URL}/api/proveedores`)
            ]);
            setRegistros(cierresRes.data);
            setInventario(invRes.data);
            setCortes(cortesRes.data);
            setProveedores(provRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleEntradaSubmit = async (e) => {
        e.preventDefault();
        if (!entradaForm.proveedor || !entradaForm.medida || !entradaForm.cantidad) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }

        try {
            const payload = {
                proveedor_id: entradaForm.proveedor,
                fecha: entradaForm.fecha,
                medida: entradaForm.medida,
                cantidad: entradaForm.cantidad,
                costo_total: 0,
                lote: entradaForm.lote
            };
            await axios.post(`${API_URL}/api/cierres/entrada`, payload);
            toast.success('Entrada registrada exitosamente');
            setEntradaModalOpen(false);
            setEntradaForm(initialEntradaForm);
            fetchData();
        } catch (error) {
            console.error('Error saving entrada:', error);
            toast.error('Error al registrar entrada');
        }
    };

    const handleSalidaSubmit = async (e) => {
        e.preventDefault();
        if (!salidaForm.maquilador || !salidaForm.corte_id || !selectedCorteInfo) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }
        const cierresTalla = selectedCorteInfo.cierres_por_talla || [];
        if (cierresTalla.length === 0) {
            toast.error('El corte no tiene cierres calculados');
            return;
        }
        const sinMedida = cierresTalla.filter(t => !tallaMedidas[t.talla]);
        if (sinMedida.length > 0) {
            toast.error(`Asigne medida de cierre a todas las tallas: ${sinMedida.map(t => 'T' + t.talla).join(', ')}`);
            return;
        }
        // Group tallas by medida
        const porMedida = {};
        cierresTalla.forEach(t => {
            const medida = tallaMedidas[t.talla];
            if (!porMedida[medida]) porMedida[medida] = { cierres: 0, detalle: [] };
            porMedida[medida].cierres += t.cierres || 0;
            porMedida[medida].detalle.push({ ...t, medida });
        });
        try {
            const salidasCreadas = [];
            for (const [medida, data] of Object.entries(porMedida)) {
                const res = await axios.post(`${API_URL}/api/cierres/salida`, {
                    fecha: salidaForm.fecha,
                    cantidad: data.cierres,
                    medida,
                    maquilador: salidaForm.maquilador,
                    corte_id: salidaForm.corte_id,
                    detalle_tallas: data.detalle
                });
                if (res?.data?.id) salidasCreadas.push(res.data.id);
            }
            toast.success('Salida registrada exitosamente');
            setSalidaModalOpen(false);
            setSalidaForm(initialSalidaForm);
            setSelectedCorteInfo(null);
            setTallaMedidas({});
            fetchData();
            // Auto-abrir remision para impresion
            if (salidasCreadas.length > 0) {
                setTimeout(() => descargarRemision(salidasCreadas[0]), 500);
            }
        } catch (error) {
            console.error('Error saving salida:', error);
            toast.error('Error al registrar salida');
        }
    };

    const descargarRemision = async (salidaId) => {
        try {
            const res = await axios.get(`${API_URL}/api/cierres/${salidaId}/remision`, {
                responseType: 'blob'
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            // Abrir en nueva pestana para imprimir
            const win = window.open(url, '_blank');
            if (!win) {
                // fallback: descargar
                const link = document.createElement('a');
                link.href = url;
                link.download = `remision_cierres.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        } catch (err) {
            toast.error('Error al descargar la remision');
        }
    };

    const handleCorteSelect = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        setSelectedCorteInfo(corte || null);
        setTallaMedidas({});
        setSalidaForm(prev => ({ ...prev, corte_id: corteId }));
    };

    const handleTallaMedidaChange = (talla, medida) => {
        setTallaMedidas(prev => ({ ...prev, [talla]: medida }));
    };

    const getCorteRelacion = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        return corte ? corte.relacion_corte : '-';
    };

    const entradas = registros.filter(r => r.tipo === 'entrada');
    const salidas = registros.filter(r => r.tipo === 'salida');
    const retornos = registros.filter(r => r.tipo === 'retorno');

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="cierres-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Control de Cierres
                    </h1>
                    <p className="text-zinc-400 mt-1">Inventario total de cierres</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => {
                            setEntradaForm(initialEntradaForm);
                            setEntradaModalOpen(true);
                        }}
                        className="bg-[#31A24C] hover:bg-[#2a8f42]"
                        data-testid="add-entrada-btn"
                    >
                        <ArrowDownCircle className="w-5 h-5 mr-2" />
                        Entrada
                    </Button>
                    <Button
                        onClick={() => {
                            setSalidaForm(initialSalidaForm);
                            setSalidaModalOpen(true);
                        }}
                        className="bg-red-500 hover:bg-red-600"
                        data-testid="add-salida-btn"
                    >
                        <ArrowUpCircle className="w-5 h-5 mr-2" />
                        Salida
                    </Button>
                </div>
            </div>

            {/* Inventory Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {MEDIDAS.map((medida) => (
                    <Card key={medida} className="card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-zinc-400">Cierres {medida}</p>
                                    <p className="text-3xl font-bold text-zinc-100 mt-1">
                                        {inventario[medida] || 0}
                                    </p>
                                    <p className="text-xs text-zinc-400 mt-1">unidades</p>
                                </div>
                                <div className="w-14 h-14 rounded-xl bg-amber-400/10 flex items-center justify-center">
                                    <Archive className="w-7 h-7 text-amber-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="entradas" className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="entradas" data-testid="tab-entradas">
                        <ArrowDownCircle className="w-4 h-4 mr-2" />
                        Entradas ({entradas.length})
                    </TabsTrigger>
                    <TabsTrigger value="salidas" data-testid="tab-salidas">
                        <ArrowUpCircle className="w-4 h-4 mr-2" />
                        Salidas ({salidas.length})
                    </TabsTrigger>
                    <TabsTrigger value="retornos" data-testid="tab-retornos">
                        <ArrowLeftCircle className="w-4 h-4 mr-2" />
                        Retornos ({retornos.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="entradas">
                    <Card className="card">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-zinc-800">
                                            <TableHead className="table-header">Fecha</TableHead>
                                            <TableHead className="table-header">Proveedor</TableHead>
                                            <TableHead className="table-header">Medida</TableHead>
                                            <TableHead className="table-header">Cantidad</TableHead>
                                            <TableHead className="table-header">Lote</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {entradas.length > 0 ? (
                                            entradas.map((registro) => (
                                                <TableRow key={registro.id} className="table-row">
                                                    <TableCell className="table-cell">{registro.fecha}</TableCell>
                                                    <TableCell className="table-cell font-medium">{registro.proveedor || registro.proveedor_id || '-'}</TableCell>
                                                    <TableCell className="table-cell">
                                                        <span className="badge-info">{registro.medida}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell">
                                                        <span className="text-emerald-400 font-medium">+{registro.cantidad}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell">{registro.lote || '-'}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center py-8 text-zinc-400">
                                                    No hay entradas registradas
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="salidas">
                    <Card className="card">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-zinc-800">
                                            <TableHead className="table-header">Fecha</TableHead>
                                            <TableHead className="table-header">Maquilador</TableHead>
                                            <TableHead className="table-header">Corte</TableHead>
                                            <TableHead className="table-header">Medida</TableHead>
                                            <TableHead className="table-header">Cantidad</TableHead>
                                            <TableHead className="table-header text-center">Remision</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {salidas.length > 0 ? (
                                            salidas.map((registro) => (
                                                <TableRow key={registro.id} className="table-row">
                                                    <TableCell className="table-cell">{registro.fecha}</TableCell>
                                                    <TableCell className="table-cell font-medium">{registro.maquilador}</TableCell>
                                                    <TableCell className="table-cell">{getCorteRelacion(registro.corte_id)}</TableCell>
                                                    <TableCell className="table-cell">
                                                        <span className="badge-info">{registro.medida || '-'}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell">
                                                        <span className="text-red-400 font-medium">-{registro.cantidad}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell text-center">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => descargarRemision(registro.id)}
                                                            className="h-7 px-2"
                                                            title="Imprimir remision"
                                                            data-testid={`btn-remision-${registro.id}`}
                                                        >
                                                            <FileText className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                                                    No hay salidas registradas
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="retornos">
                    <Card className="card">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-zinc-800">
                                            <TableHead className="table-header">Fecha</TableHead>
                                            <TableHead className="table-header">Cantidad Recibida</TableHead>
                                            <TableHead className="table-header">Diferencias</TableHead>
                                            <TableHead className="table-header">Observaciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {retornos.length > 0 ? (
                                            retornos.map((registro) => (
                                                <TableRow key={registro.id} className="table-row">
                                                    <TableCell className="table-cell">{registro.fecha}</TableCell>
                                                    <TableCell className="table-cell font-medium">{registro.cantidad}</TableCell>
                                                    <TableCell className="table-cell">
                                                        {registro.diferencias > 0 ? (
                                                            <span className="text-red-400 font-medium">{registro.diferencias}</span>
                                                        ) : (
                                                            <span className="text-emerald-400">0</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="table-cell">{registro.observaciones || '-'}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center py-8 text-zinc-400">
                                                    No hay retornos registrados
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Entrada Modal */}
            <Dialog open={entradaModalOpen} onOpenChange={setEntradaModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="entrada-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <ArrowDownCircle className="w-5 h-5 inline mr-2 text-emerald-400" />
                            Nueva Entrada de Cierres
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEntradaSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input
                                    type="date"
                                    value={entradaForm.fecha}
                                    onChange={(e) => setEntradaForm({ ...entradaForm, fecha: e.target.value })}
                                    data-testid="entrada-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Medida *</Label>
                                <Select 
                                    value={entradaForm.medida} 
                                    onValueChange={(value) => setEntradaForm({ ...entradaForm, medida: value })}
                                >
                                    <SelectTrigger data-testid="entrada-medida">
                                        <SelectValue placeholder="Seleccionar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MEDIDAS.map((m) => (
                                            <SelectItem key={m} value={m}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Proveedor *</Label>
                            <Input
                                value={entradaForm.proveedor}
                                onChange={(e) => setEntradaForm({ ...entradaForm, proveedor: e.target.value })}
                                placeholder="Nombre del proveedor"
                                data-testid="entrada-proveedor"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Cantidad *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={entradaForm.cantidad}
                                    onChange={(e) => setEntradaForm({ ...entradaForm, cantidad: parseInt(e.target.value) || 0 })}
                                    data-testid="entrada-cantidad"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Lote</Label>
                                <Input
                                    value={entradaForm.lote}
                                    onChange={(e) => setEntradaForm({ ...entradaForm, lote: e.target.value })}
                                    placeholder="Número de lote"
                                    data-testid="entrada-lote"
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEntradaModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-[#31A24C] hover:bg-[#2a8f42]" data-testid="save-entrada-btn">
                                Registrar Entrada
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Salida Modal */}
            <Dialog open={salidaModalOpen} onOpenChange={(open) => { setSalidaModalOpen(open); if (!open) { setSelectedCorteInfo(null); setTallaMedidas({}); } }}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="salida-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <ArrowUpCircle className="w-5 h-5 inline mr-2 text-red-400" />
                            Nueva Salida de Cierres
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSalidaSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Fecha *</Label>
                            <Input
                                type="date"
                                value={salidaForm.fecha}
                                onChange={(e) => setSalidaForm({ ...salidaForm, fecha: e.target.value })}
                                data-testid="salida-fecha"
                            />
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Maquilador *</Label>
                            <Input
                                value={salidaForm.maquilador}
                                onChange={(e) => setSalidaForm({ ...salidaForm, maquilador: e.target.value })}
                                placeholder="Nombre del maquilador"
                                data-testid="salida-maquilador"
                            />
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Corte Relacionado *</Label>
                            <Select
                                value={salidaForm.corte_id}
                                onValueChange={handleCorteSelect}
                            >
                                <SelectTrigger data-testid="salida-corte">
                                    <SelectValue placeholder="Seleccionar corte" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cortes.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.relacion_corte} - {c.marca}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {selectedCorteInfo && (
                            <div className="p-4 bg-zinc-800 rounded-lg space-y-3" data-testid="cierres-auto-calculo">
                                <p className="text-sm font-semibold text-zinc-200">Asignar medida de cierre por talla</p>
                                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                                    <p className="text-zinc-400">Total paquetes: <span className="text-amber-400 font-semibold">{selectedCorteInfo.cantidad_total}</span></p>
                                    <p className="text-zinc-400">Piezas cortadas: <span className="text-amber-400 font-semibold">{selectedCorteInfo.piezas_cortadas || 0}</span></p>
                                </div>
                                {(selectedCorteInfo.cierres_por_talla || []).length > 0 ? (
                                    <>
                                        <div className="space-y-2">
                                            {selectedCorteInfo.cierres_por_talla.map((t, i) => (
                                                <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-zinc-900/50">
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-sm font-medium text-zinc-200">T{t.talla}</span>
                                                        <span className="text-xs text-zinc-500 ml-2">{t.paquetes} paq = {t.cierres} cierres</span>
                                                    </div>
                                                    <Select
                                                        value={tallaMedidas[t.talla] || ''}
                                                        onValueChange={(v) => handleTallaMedidaChange(t.talla, v)}
                                                    >
                                                        <SelectTrigger className="w-40" data-testid={`medida-talla-${t.talla}`}>
                                                            <SelectValue placeholder="Medida" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {MEDIDAS.map((m) => (
                                                                <SelectItem key={m} value={m}>{m} (Stock: {inventario[m] || 0})</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            ))}
                                        </div>
                                        {/* Summary by medida */}
                                        {Object.keys(tallaMedidas).length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-zinc-700 space-y-1">
                                                <p className="text-xs text-zinc-400 uppercase tracking-wider">Resumen de descuento</p>
                                                {Object.entries(
                                                    (selectedCorteInfo.cierres_por_talla || []).reduce((acc, t) => {
                                                        const m = tallaMedidas[t.talla];
                                                        if (m) { acc[m] = (acc[m] || 0) + (t.cierres || 0); }
                                                        return acc;
                                                    }, {})
                                                ).map(([medida, total]) => (
                                                    <p key={medida} className="text-sm">
                                                        <span className="badge-info mr-2">{medida}</span>
                                                        <span className="text-red-400 font-bold">-{total} cierres</span>
                                                        <span className="text-zinc-500 ml-2">(Stock actual: {inventario[medida] || 0})</span>
                                                    </p>
                                                ))}
                                                <p className="text-sm text-zinc-200 mt-2 pt-2 border-t border-zinc-700">
                                                    Total cierres: <span className="font-bold text-red-400 text-lg">
                                                        {(selectedCorteInfo.cierres_por_talla || []).reduce((sum, t) => sum + (tallaMedidas[t.talla] ? (t.cierres || 0) : 0), 0)}
                                                    </span>
                                                </p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-xs text-yellow-400">Este corte no tiene cierres calculados. Verifique que tenga piezas cortadas y paquetes registrados.</p>
                                )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => { setSalidaModalOpen(false); setSelectedCorteInfo(null); setTallaMedidas({}); }}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="save-salida-btn"
                                disabled={!selectedCorteInfo || (selectedCorteInfo.cierres_por_talla || []).some(t => !tallaMedidas[t.talla])}>
                                Registrar Salida
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
